import { useEffect, useState } from "react";
import { BoatWithTeamDetails } from "../../types/boat.types";
import { BoatsAPI } from "../../APIs/boats.api";
import { useErrorHandler } from "../../hooks/useErrorHandler";
import { ErrorMessage } from "../../components/error-message";
import CrudTable from "../../components/crud-table";
import { useTableControls } from "../../hooks/useTableControls";
import { capitalize } from "../../utils/capitalize.utils";
import { CrudTableHeader } from "../../components/crud-table-header";
import { ShipIcon } from "../../components/boat-icon";
import { useCompetitions } from "../../context/CompetitionsContext";
import { TeamLink } from "../../components/team-link";
import { Loading } from "../../components/loading";

export function BoatsTableAdmins() {
  const [boats, setBoats] = useState<BoatWithTeamDetails[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { error, handleError, clearError } = useErrorHandler();

  const {
    paginatedData: paginatedBoats,
    filteredData,
    totalPages,
    currentPage,
    sortConfig,
    setSortConfig,
    setSearchQuery,
    setCurrentPage,
    setSelectedFilters,
  } = useTableControls<BoatWithTeamDetails>(boats);

  const { currentCompetitionId } = useCompetitions();

  const columns = [
    {
      key: "boat_id" as keyof BoatWithTeamDetails,
      label: "N°",
      sortable: true,
    },
    {
      key: "boat_name" as keyof BoatWithTeamDetails,
      label: "Embarcación",
      sortable: true,
    },
    {
      key: "team_id" as keyof BoatWithTeamDetails,
      label: "N° Equipo",
      sortable: true,
    },
    {
      key: "team_name" as keyof BoatWithTeamDetails,
      label: "Equipo",
      sortable: true,
      render: (value: string, row: BoatWithTeamDetails) => (
        <TeamLink teamName={value} teamId={row.team_id} />
      ),
    },
    {
      key: "registration_number" as keyof BoatWithTeamDetails,
      label: "Matricula",
      sortable: true,
    },
    {
      key: "engine_brand" as keyof BoatWithTeamDetails,
      label: "Marca",
      sortable: true,
    },
    { key: "hp" as keyof BoatWithTeamDetails, label: "HP", sortable: true },
    {
      key: "has_radio" as keyof BoatWithTeamDetails,
      label: "Radio",
      render: (value: boolean) => (value ? "Sí" : "No"),
    },
    {
      key: "is_yacht" as keyof BoatWithTeamDetails,
      label: "Yate",
      render: (value: boolean) => (value ? "Sí" : "No"),
    },
  ];

  useEffect(() => {
    if (!currentCompetitionId) {
      return;
    }
    const fetchBoats = async () => {
      try {
        setIsLoading(true);
        clearError();

        const response = await BoatsAPI.getAllBoats(currentCompetitionId);
        if (response.statusCode === 200) {
          setBoats(response.data);
        } else {
          handleError(response.message, { persist: true });
        }
      } catch (err) {
        handleError("Error de conexión, intente nuevamente.", {
          persist: true,
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchBoats();
  }, [handleError, currentCompetitionId, clearError]);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  const handleSort = (
    sortBy: string | null,
    sortDirection: "asc" | "desc" | null
  ) => {
    setSortConfig({
      sortBy: sortBy as keyof BoatWithTeamDetails | null,
      sortDirection,
    });
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleFilterChange = (selectedFilters: Record<string, string[]>) => {
    setSelectedFilters(selectedFilters);
  };

  function getUniqueEngineBrandMap(
    boats: BoatWithTeamDetails[]
  ): Record<string, string> {
    const brandMap: Record<string, string> = {};

    boats.forEach((boat) => {
      if (boat.engine_brand) {
        const normalizedKey = boat.engine_brand.trim().toLowerCase();

        const displayValue = capitalize(boat.engine_brand.trim());

        brandMap[normalizedKey] = displayValue;
      }
    });

    return brandMap;
  }

  return (
    <div className="w-full p-8">
      <CrudTableHeader
        title="Embarcaciones"
        count={boats.length}
        icon={<ShipIcon />}
      />

      {error && (
        <div className="mb-4">
          <ErrorMessage message={error} dismissible />
        </div>
      )}

      {isLoading ? (
        <Loading />
      ) : boats ? (
        <CrudTable
          data={paginatedBoats}
          columns={columns}
          search={{
            placeholder: "Buscar embarcaciones...",
            onSearch: handleSearch,
          }}
          sort={{
            sortBy: sortConfig.sortBy,
            sortDirection: sortConfig.sortDirection,
            onSort: handleSort,
          }}
          pagination={{
            page: currentPage,
            totalPages: totalPages,
            onPageChange: handlePageChange,
            labels: {
              next: "Siguiente",
              previous: "Anterior",
              pageInfo: `Página ${currentPage} de ${totalPages}`,
            },
          }}
          filters={[
            {
              key: "is_yacht",
              label: "Yate",
              multiple: false,
              options: [
                { key: "1", value: "Sí" },
                { key: "0", value: "No" },
              ],
            },
            {
              key: "has_radio",
              label: "Radio",
              multiple: false,
              options: [
                { key: "1", value: "Sí" },
                { key: "0", value: "No" },
              ],
            },
            {
              key: "engine_brand",
              label: "Marca de motor",
              multiple: true,
              options: Object.entries(getUniqueEngineBrandMap(boats)).map(
                ([key, value]) => ({
                  key,
                  value,
                })
              ),
            },
          ]}
          onFilterChange={handleFilterChange}
          totalCount={filteredData.length}
        />
      ) : null}
    </div>
  );
}
